import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, LifeBuoy, Mail } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

const Help = () => {
  const { isAuthenticated, viewMode } = useAuthStore();

  const docs = [
    {
      id: 'user-help',
      title: 'User Help Guide',
      description: 'Step-by-step guidance for end users completing verification.',
      allowedViews: ['platform_admin', 'company_admin', 'end_user']
    },
    {
      id: 'admin-guide',
      title: 'Administrator Guide',
      description: 'Platform and company admin workflows, settings, and best practices.',
      allowedViews: ['platform_admin', 'company_admin']
    },
    {
      id: 'technical',
      title: 'Technical Documentation',
      description: 'API reference and implementation details for developers.',
      allowedViews: ['platform_admin']
    }
  ];

  const getDocLink = (docId) => (isAuthenticated ? `/docs/${docId}` : '/login');

  const shouldShowDoc = (doc) => {
    if (!isAuthenticated || !viewMode) {
      return true;
    }
    return doc.allowedViews.includes(viewMode);
  };

  return (
    <div className="py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help & Support</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers, documentation, and support options for Acquiva.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Documentation</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Browse guides tailored to your role. Some documentation requires an account.
            </p>
            <div className="space-y-4">
              {docs.filter(shouldShowDoc).map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    {!isAuthenticated && (
                      <span className="text-xs text-gray-500">Sign in required</span>
                    )}
                    <Link
                      to={getDocLink(doc.id)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      {isAuthenticated ? 'Open guide' : 'Sign in to view'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <LifeBuoy className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Common Help Topics</h2>
              </div>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Feedback submissions are sent to our support team for review.</li>
                <li>Verification links expire after 14 days for security.</li>
                <li>For access issues, confirm your role permissions with an admin.</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Contact Support</h2>
              </div>
              <p className="text-gray-600 mb-4">
                Need more help? Our support team can assist with verification issues and platform access.
              </p>
              <a
                href="mailto:support@acquiva.com"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                Email support@acquiva.com
              </a>
              <div className="mt-3 text-sm text-gray-500">
                Or <Link to="/contact" className="text-blue-600 hover:text-blue-700">send us a message</Link>.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
